import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, User, BookOpen, Target, Sparkles } from 'lucide-react';
import { useData } from '../context/DataContext';
import useScopedStudents from '../hooks/useScopedStudents';
import SessionPlannerInsights from '../components/SessionPlannerInsights';
import PlayPracticePlanBuilder from '../components/PlayPracticePlanBuilder';
import SessionHistoryPanel from '../components/SessionHistoryPanel';
import StudentSelector from '../components/StudentSelector';
import HelperIcon from '../components/HelperIcon';
import { helperContent } from '../content/helper-content';
import '../styles/session-planner.css';

const SessionPlanner = () => {
  const [searchParams] = useSearchParams();

  // Use DataContext for planner/session context payloads
  const {
    getPlannerData,
    getStudentContext
  } = useData();
  const { students, studentsLoading } = useScopedStudents();

  const [plannerData, setPlannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Student selection state
  const [selectedStudentUuid, setSelectedStudentUuid] = useState(null);
  const [studentContext, setStudentContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  // Collapsible sections state
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Load data using cached context methods
  const loadPlannerData = useCallback(async (studentUuid = null) => {
    try {
      setLoading(true);
      const data = await getPlannerData(studentUuid);
      setPlannerData(data);
      setError(null);
    } catch (err) {
      console.error('Error loading planner data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getPlannerData]);

  const loadStudentContext = useCallback(async (uuid) => {
    try {
      setContextLoading(true);
      const data = await getStudentContext(uuid);
      setStudentContext(data);
    } catch (err) {
      console.error('Error loading student context:', err);
    } finally {
      setContextLoading(false);
    }
  }, [getStudentContext]);

  // Load initial planner data on mount
  useEffect(() => {
    loadPlannerData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload planner data when student selection changes
  useEffect(() => {
    if (selectedStudentUuid) {
      loadPlannerData(selectedStudentUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentUuid]);

  // Check for student param from URL (from Student Roster "Plan Session" button)
  useEffect(() => {
    const studentParam = searchParams.get('student');
    if (studentParam && students.length > 0) {
      const exists = students.find(s => s.uuid === studentParam);
      if (exists) {
        setSelectedStudentUuid(studentParam);
      }
    }
  }, [searchParams, students]);

  // Clear stale selection when scoped student list changes.
  useEffect(() => {
    if (!selectedStudentUuid) return;
    const exists = students.some(s => s.uuid === selectedStudentUuid);
    if (!exists) {
      setSelectedStudentUuid(null);
      setStudentContext(null);
    }
  }, [students, selectedStudentUuid]);

  // Load student context when selection changes
  useEffect(() => {
    if (selectedStudentUuid) {
      loadStudentContext(selectedStudentUuid);
    } else {
      setStudentContext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentUuid]);

  // Get student display name
  const getStudentName = () => {
    if (!studentContext?.profile) return '';
    const { first_name, last_name } = studentContext.profile;
    return `${first_name || ''} ${last_name || ''}`.trim();
  };

  if (loading && !plannerData) {
    return (
      <div className="session-planner">
        <div className="sp-loading">
          <div className="lrbrain-typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="sp-loading__text">Loading session planner...</p>
        </div>
      </div>
    );
  }

  if (error && !plannerData) {
    return (
      <div className="session-planner">
        <div className="sp-loading">
          <p className="sp-text" style={{ color: 'var(--lrbrain-error)' }}>Error: {error}</p>
          <button onClick={() => loadPlannerData(selectedStudentUuid)} className="sp-btn sp-btn--accent">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { insights } = plannerData || {};

  return (
    <div className="session-planner">
      {/* Header */}
      <header className="session-planner__header">
        <div className="session-planner__header-content">
          <div className="session-planner__title-group">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h1 className="session-planner__title">Session Planner</h1>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '0.25rem 0.5rem',
                background: 'var(--lrbrain-accent)',
                color: 'white',
                borderRadius: '4px'
              }}>
                90 MIN
              </span>
              <HelperIcon
                title={helperContent.sessionPlanning?.title || "Session Planning"}
                content={helperContent.sessionPlanning?.content || "AI-powered session planning using Play-Practice-Play format."}
                size="small"
              />
            </div>
            <p className="session-planner__subtitle">
              {selectedStudentUuid
                ? `Building a personalized session for ${getStudentName() || 'student'}`
                : `Select a student to auto-generate a Play-Practice-Play session plan`
              }
            </p>
            <div className="session-planner__selector">
              <StudentSelector
                students={students}
                selectedStudentUuid={selectedStudentUuid}
                onSelect={setSelectedStudentUuid}
                loading={studentsLoading}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Dense single-column layout */}
      <div className="session-planner__grid--dense">
        {/* Context Ribbon - compact student info */}
        {selectedStudentUuid && studentContext?.profile && (
          <ContextRibbon
            student={studentContext.profile}
            sessionCount={insights?.total_sessions || studentContext?.transcripts_count || 0}
            loading={contextLoading}
          />
        )}

        {/* Main Builder - Play-Practice-Play */}
        <PlayPracticePlanBuilder
          studentUuid={selectedStudentUuid}
          studentName={getStudentName()}
          insights={insights}
        />

        {/* Collapsible Insights Section */}
        <div className="sp-collapsible-wrapper">
          <button
            className="sp-collapsible-wrapper__header"
            onClick={() => setInsightsExpanded(!insightsExpanded)}
          >
            <span className="sp-collapsible-wrapper__title">
              {insightsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Sparkles size={14} />
              Insights & Recommendations
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--lrbrain-text-light)' }}>
              What's worked, challenges, focus areas
            </span>
          </button>
          {insightsExpanded && (
            <div className="sp-collapsible-wrapper__content">
              <SessionPlannerInsights insights={insights} />
            </div>
          )}
        </div>

        {/* Collapsible Session History */}
        <div className="sp-collapsible-wrapper">
          <button
            className="sp-collapsible-wrapper__header"
            onClick={() => setHistoryExpanded(!historyExpanded)}
          >
            <span className="sp-collapsible-wrapper__title">
              {historyExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <BookOpen size={14} />
              Session History
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--lrbrain-text-light)' }}>
              Browse past sessions
            </span>
          </button>
          {historyExpanded && (
            <div className="sp-collapsible-wrapper__content">
              <SessionHistoryPanel
                onSessionSelect={() => {}}
                selectedSessionId={null}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ContextRibbon - Compact student info bar
 */
const ContextRibbon = ({ student, sessionCount, loading }) => {
  if (loading) {
    return (
      <div className="sp-context-ribbon">
        <span style={{ color: 'var(--lrbrain-text-light)', fontSize: '0.8125rem' }}>
          Loading student info...
        </span>
      </div>
    );
  }

  return (
    <div className="sp-context-ribbon">
      <div className="sp-context-ribbon__item">
        <User size={14} style={{ color: 'var(--lrbrain-text-light)' }} />
        <span className="sp-context-ribbon__value">
          {student.first_name} {student.last_name}
        </span>
      </div>

      {student.grade_year_level && (
        <>
          <div className="sp-context-ribbon__divider" />
          <div className="sp-context-ribbon__item">
            <span className="sp-context-ribbon__label">Grade</span>
            <span className="sp-context-ribbon__value">{student.grade_year_level}</span>
          </div>
        </>
      )}

      {student.target_subjects && (
        <>
          <div className="sp-context-ribbon__divider" />
          <div className="sp-context-ribbon__item">
            <BookOpen size={14} style={{ color: 'var(--lrbrain-text-light)' }} />
            <span className="sp-context-ribbon__value">{student.target_subjects}</span>
          </div>
        </>
      )}

      {student.learning_style && (
        <>
          <div className="sp-context-ribbon__divider" />
          <div className="sp-context-ribbon__item">
            <span className="sp-context-ribbon__label">Style</span>
            <span className="sp-context-ribbon__value">{student.learning_style}</span>
          </div>
        </>
      )}

      {sessionCount > 0 && (
        <>
          <div className="sp-context-ribbon__divider" />
          <div className="sp-context-ribbon__item">
            <Target size={14} style={{ color: 'var(--lrbrain-text-light)' }} />
            <span className="sp-context-ribbon__value">{sessionCount} sessions</span>
          </div>
        </>
      )}
    </div>
  );
};

export default SessionPlanner;
